import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, PageTitle } from '../ui';
import { supabase } from '@/integrations/supabase/client';
import { crmStore, nextPrestIdForClient, useCrm } from '../store';
import { toast } from 'sonner';
import {
  searchAddress, calcRoute, formatDuree, type NominatimSuggestion, type Coords,
} from '../utils/geo';
import {
  Sparkles, ArrowLeft, ArrowRight, Check, MapPin, Loader2, User, Calendar,
  Mic2, FileText, Search, Plus, X, Map, Calculator,
} from 'lucide-react';
import ClientForm from '../components/ClientForm';
import IntervenantsPanel from '../components/IntervenantsPanel';
import type { Client } from '../types';
import { withDemoFlag, withDemoFlagArray, demoFilterValue, useDemoMode } from '../demoMode';
import { useStructureFilter } from '../structureContext';
import { getArtistesIndisponibles, formatConflitMessage, type ConflitArtiste } from '../lib/disponibilites';
import { useConfirm } from '../components/ConfirmDialog';
import { ProduitsPicker, produitsTotals, type ProduitLigne } from '../components/ProduitsPicker';
import { TvaSelect } from '../components/TvaSelect';
import { normalizeAdresse } from '@/crm/utils/adresse';

type Step = 1 | 2 | 3 | 4 | 5;

interface ClientLite {
  id: string;
  nom: string;
  prenom: string | null;
  societe: string | null;
  email: string | null;
  tel: string | null;
  telSecondaire?: string | null;
  ville: string | null;
  adresse?: string | null;
  cp?: string | null;
  pays?: string | null;
  fonction?: string | null;
  siret?: string | null;
  tvaIntra?: string | null;
  segment?: string | null;
  source?: string | null;
  notes?: string | null;
  typeClient?: string | null;
}

interface ArtisteLite {
  id: string;
  nom_scene: string;
  domaine_principal: string | null;
  domaines: string[] | null;
  tarif_base: number | null;
  frais_deplacement: number | null;
  tva_pct: number | null;
  ville: string | null;
  lat: number | null;
  lng: number | null;
}

interface ArtisteCalc {
  tarifHT: number;       // tarif de base HT
  margePct: number;      // marge %
  fraisDepl: number;     // frais déplacement
  tvaPct: number;        // TVA %
  vhrDepl: number;       // VHR déplacement
  vhrHotel: number;      // VHR hôtel
  vhrProd: number;       // VHR production
  opt1Lib: string; opt1HT: number; opt1On: boolean; opt1Tarif?: number; opt1Marge?: number; opt1Note?: string;
  opt2Lib: string; opt2HT: number; opt2On: boolean; opt2Tarif?: number; opt2Marge?: number; opt2Note?: string;
  opt3Lib: string; opt3HT: number; opt3On: boolean; opt3Tarif?: number; opt3Marge?: number; opt3Note?: string;
  opt4Lib: string; opt4HT: number; opt4On: boolean; opt4Tarif?: number; opt4Marge?: number; opt4Note?: string;
  prixLibreHT?: number;     // override manuel du Vendu HT (0/undefined = auto calculé)
}

// Calcule le coût HT réel d'une option : tarif artiste sans marge.
export function optionCostHT(c: ArtisteCalc, n: 1 | 2 | 3 | 4): number {
  const libre = Number((c as any)[`opt${n}HT`]) || 0;
  if (libre > 0) {
    const marge = Number((c as any)[`opt${n}Marge`]) || 0;
    return marge > 0 ? libre / (1 + marge / 100) : libre;
  }
  return Number((c as any)[`opt${n}Tarif`]) || 0;
}

// Calcule le HT final vendu d'une option : libre (opt{n}HT) prioritaire, sinon tarif × (1 + marge%)
export function optionFinalHT(c: ArtisteCalc, n: 1 | 2 | 3 | 4): number {
  const libre = Number((c as any)[`opt${n}HT`]) || 0;
  if (libre > 0) return libre;
  const tarif = Number((c as any)[`opt${n}Tarif`]) || 0;
  const marge = Number((c as any)[`opt${n}Marge`]) || 0;
  return tarif * (1 + marge / 100);
}

interface SelectedArtist extends ArtisteLite {
  km?: number;
  minutes?: number;
  calc: ArtisteCalc;
  included: boolean; // inclus dans le calcul / la proposition finale
}

const defaultCalc = (a: ArtisteLite): ArtisteCalc => ({
  tarifHT: a.tarif_base ?? 0,
  margePct: 30,
  fraisDepl: a.frais_deplacement ?? 0,
  tvaPct: a.tva_pct ?? 20,
  vhrDepl: 0, vhrHotel: 0, vhrProd: 0,
  opt1Lib: '', opt1HT: 0, opt1On: false, opt1Tarif: 0, opt1Marge: 0, opt1Note: '',
  opt2Lib: '', opt2HT: 0, opt2On: false, opt2Tarif: 0, opt2Marge: 0, opt2Note: '',
  opt3Lib: '', opt3HT: 0, opt3On: false, opt3Tarif: 0, opt3Marge: 0, opt3Note: '',
  opt4Lib: '', opt4HT: 0, opt4On: false, opt4Tarif: 0, opt4Marge: 0, opt4Note: '',
  prixLibreHT: 0,
});

// Calcul prix artiste : marge appliquée séparément sur le tarif artiste et sur chaque option.
function computeArtistePrice(c: ArtisteCalc) {
  const optsCostTotal = (c.opt1On ? optionCostHT(c, 1) : 0) + (c.opt2On ? optionCostHT(c, 2) : 0) + (c.opt3On ? optionCostHT(c, 3) : 0) + (c.opt4On ? optionCostHT(c, 4) : 0);
  const optsVenduTotal = (c.opt1On ? optionFinalHT(c, 1) : 0) + (c.opt2On ? optionFinalHT(c, 2) : 0) + (c.opt3On ? optionFinalHT(c, 3) : 0) + (c.opt4On ? optionFinalHT(c, 4) : 0);
  const baseCostHT = c.tarifHT + c.fraisDepl + c.vhrDepl + c.vhrHotel + c.vhrProd;
  const coutHT = baseCostHT + optsCostTotal;
  // Marge en majoration : Vendu HT = base × (1 + marge%) + options déjà margées.
  const venduHTAuto = (baseCostHT * (1 + (c.margePct || 0) / 100)) + optsVenduTotal;
  const libre = Number(c.prixLibreHT) || 0;
  const venduHT = libre > 0 ? libre : venduHTAuto;
  const venduTTC = venduHT * (1 + (c.tvaPct || 0) / 100);
  return { coutHT, venduHT, venduTTC, venduHTAuto, isLibre: libre > 0 };
}

const inputCls = 'w-full px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-amber-500/50';

export default function NouvelleDemande() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editNumero = searchParams.get('edit');
  const isEdit = !!editNumero;
  const [editDbId, setEditDbId] = useState<string | null>(null);
  const [hydrating, setHydrating] = useState(isEdit);
  const data = useCrm();
  const { filter: structureFilter } = useStructureFilter();
  const [step, setStep] = useState<Step>(1);
  const [saving, setSaving] = useState(false);

  // ---- Étape 1 : Client ----
  const [clients, setClients] = useState<ClientLite[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientLite | null>(null);
  const [openClientForm, setOpenClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  const openEditClient = useCallback(async () => {
    if (!selectedClient) return;
    // Charge la fiche complète depuis Supabase
    const { data: row } = await supabase
      .from('clients')
      .select('*')
      .eq('id', selectedClient.id)
      .maybeSingle();
    if (!row) { toast.error('Fiche client introuvable'); return; }
    setEditingClient({
      id: row.id,
      typeClient: row.type_client,
      nom: row.nom || '',
      prenom: row.prenom || '',
      societe: row.societe || '',
      fonction: row.fonction || '',
      email: row.email || '',
      tel: row.tel || '',
      telSecondaire: row.tel_secondaire || '',
      adresse: row.adresse || '',
      cp: row.cp || '',
      ville: row.ville || '',
      departement: row.departement || '',
      pays: row.pays || 'France',
      siret: row.siret || '',
      tvaIntra: row.tva_intra || '',
      source: row.source || '',
      segment: row.segment || '',
      tags: row.tags || [],
      potentielBusiness: row.potentiel_business || '',
      notes: row.notes || '',
    } as Client);
    setOpenClientForm(true);
  }, [selectedClient]);

  // ---- Étape 2 : Événement ----
  const [eventForm, setEventForm] = useState({
    dateEvent: '',
    duree: '4h',
    libelle: 'Mariage',
    demande: 'Site Web',
    services: [] as string[],
    domaines: [] as string[],
    themes: [] as string[],
    remarques: '',
  });
  const [lieuQuery, setLieuQuery] = useState('');
  const [lieuCoords, setLieuCoords] = useState<Coords | null>(null);
  const [agencyKm, setAgencyKm] = useState<number | null>(null);
  const [agencyDuree, setAgencyDuree] = useState<string>('');

  // ---- Étape 3 : Artistes ----
  const [artistesDb, setArtistesDb] = useState<ArtisteLite[]>([]);
  const [artistDistances, setArtistDistances] = useState<Record<string, { km: number; minutes: number }>>({});
  const [computingDistances, setComputingDistances] = useState(false);
  const [filterDomaine, setFilterDomaine] = useState<string>('');
  const [selectedArtists, setSelectedArtists] = useState<SelectedArtist[]>([]);
  const [conflitsArtistes, setConflitsArtistes] = useState<Record<string, ConflitArtiste>>({});
  const [produitLignes, setProduitLignes] = useState<ProduitLigne[]>([]);
  const [showBookedArtists, setShowBookedArtists] = useState(false);
  const confirm = useConfirm();

  // ---- Étape 5 : Récap ----
  const [propTTC, setPropTTC] = useState('');
  const [propTTCManual, setPropTTCManual] = useState(false); // true si l'utilisateur a édité manuellement
  const [propNote, setPropNote] = useState('');

  const [demoMode] = useDemoMode();

  // Charge les clients (pour autocomplete) — filtré selon mode démo
  const loadClients = useCallback(async () => {
    const { data: rows } = await supabase
      .from('clients')
      .select('id, nom, prenom, societe, email, tel, tel_secondaire, ville, adresse, cp, pays, fonction, siret, tva_intra, segment, source, notes, type_client')
      .is('deleted_at', null)
      .eq('is_demo', demoFilterValue())
      .order('nom')
      .limit(500);
    if (rows) {
      setClients(rows.map((r: any) => ({
        id: r.id,
        nom: r.nom, prenom: r.prenom, societe: r.societe, email: r.email, tel: r.tel,
        telSecondaire: r.tel_secondaire, ville: r.ville, adresse: r.adresse, cp: r.cp,
        pays: r.pays, fonction: r.fonction, siret: r.siret, tvaIntra: r.tva_intra,
        segment: r.segment, source: r.source, notes: r.notes, typeClient: r.type_client,
      })));
    }
  }, [demoMode]);
  useEffect(() => { loadClients(); }, [loadClients]);

  // Callback après création/modification d'un client via le formulaire complet
  const handleClientSaved = useCallback(async (saved: Client) => {
    await loadClients();
    const lite: ClientLite = {
      id: saved.id,
      nom: saved.nom || '',
      prenom: saved.prenom || null,
      societe: saved.societe || null,
      email: saved.email || null,
      tel: saved.tel || null,
      telSecondaire: (saved as any).telSecondaire || null,
      ville: saved.ville || null,
      adresse: (saved as any).adresse || null,
      cp: (saved as any).cp || null,
      pays: (saved as any).pays || null,
      fonction: (saved as any).fonction || null,
      siret: (saved as any).siret || null,
      tvaIntra: (saved as any).tvaIntra || null,
      segment: (saved as any).segment || null,
      source: (saved as any).source || null,
      notes: (saved as any).notes || null,
      typeClient: (saved as any).typeClient || null,
    };
    setSelectedClient(lite);
    setClientSearch('');
  }, [loadClients]);

  // Charge les artistes
  useEffect(() => {
    (async () => {
      const { data: rows } = await supabase
        .from('artistes')
        .select('id, nom_scene, domaine_principal, domaines, tarif_base, frais_deplacement, tva_pct, ville, lat, lng')
        .is('deleted_at', null)
        .order('nom_scene');
      if (rows) setArtistesDb(rows as ArtisteLite[]);
    })();
  }, []);

  // ---- Hydratation mode édition ----
  useEffect(() => {
    if (!isEdit || !editNumero) return;
    (async () => {
      try {
        const { data: prest } = await supabase
          .from('prestations')
          .select('*')
          .eq('numero', editNumero)
          .maybeSingle();
        if (!prest) {
          toast.error('Prestation introuvable');
          navigate('/crm-pro-evenementiel/prestations');
          return;
        }
        setEditDbId(prest.id);

        // Client
        if (prest.client_id) {
          const { data: c } = await supabase
            .from('clients')
            .select('id, nom, prenom, societe, email, tel, tel_secondaire, ville, adresse, cp, pays, fonction, siret, tva_intra, segment, source, notes, type_client')
            .eq('id', prest.client_id)
            .maybeSingle();
          if (c) {
            setSelectedClient({
              id: c.id, nom: c.nom, prenom: c.prenom, societe: c.societe, email: c.email, tel: c.tel,
              telSecondaire: c.tel_secondaire, ville: c.ville, adresse: c.adresse, cp: c.cp, pays: c.pays,
              fonction: c.fonction, siret: c.siret, tvaIntra: c.tva_intra, segment: c.segment,
              source: c.source, notes: c.notes, typeClient: c.type_client,
            });
          }
        }

        // Événement
        setEventForm({
          dateEvent: prest.date_event || '',
          duree: prest.duree || '4h',
          libelle: prest.libelle || 'Mariage',
          demande: prest.demande || 'Site Web',
          services: (prest.services as string[]) || [],
          domaines: (prest.domaines as string[]) || [],
          themes: (prest.themes as string[]) || [],
          remarques: prest.remarques || '',
        });
        setLieuQuery(prest.lieu || '');
        if (prest.km != null) setAgencyKm(prest.km);

        // Artistes liés
        const { data: links } = await supabase
          .from('prestations_artistes')
          .select('artiste_id, cout, prop_ttc, marge_pct, km, duree_trajet, calc_data, inclus')
          .eq('prestation_id', prest.id);
        if (links && links.length) {
          // On charge les fiches artistes pour récupérer les détails
          const ids = links.map((l: any) => l.artiste_id).filter(Boolean);
          const { data: arts } = await supabase
            .from('artistes')
            .select('id, nom_scene, domaine_principal, domaines, tarif_base, frais_deplacement, tva_pct, ville, lat, lng')
            .in('id', ids);
          const byId: Record<string, ArtisteLite> = {};
          (arts || []).forEach((a: any) => { byId[a.id] = a as ArtisteLite; });
          const sels: SelectedArtist[] = links.map((l: any): SelectedArtist | null => {
            const a = byId[l.artiste_id];
            if (!a) return null;
            // Si on a le calc complet sauvegardé, on le restaure tel quel (options, tarif, marge, frais…)
            const saved = l.calc_data as Partial<ArtisteCalc> | null;
            const calc: ArtisteCalc = saved && typeof saved === 'object'
              ? { ...defaultCalc(a), ...saved }
              : (() => {
                  // Legacy : pas de calc_data → on rebuild avec marge + override TTC
                  const c = defaultCalc(a);
                  c.margePct = l.marge_pct ?? c.margePct;
                  // `prop_ttc` stocke en réalité le vendu HT — on le restaure tel quel.
                  c.prixLibreHT = l.prop_ttc ? Number(l.prop_ttc) : 0;
                  return c;
                })();
            return {
              ...a,
              km: l.km ?? undefined,
              calc,
              included: l.inclus !== false,
            };
          }).filter(Boolean) as SelectedArtist[];
          setSelectedArtists(sels);
        }

        // Produits / locations liés
        const { data: prodRows } = await (supabase.from('prestations_produits' as any) as any)
          .select('*')
          .eq('prestation_id', prest.id)
          .order('ordre');
        if (prodRows && prodRows.length) {
          setProduitLignes(prodRows.map((r: any, i: number) => ({
            id: r.id,
            produitId: r.produit_id || null,
            libelle: r.libelle || '',
            description: r.description || '',
            quantite: Number(r.quantite ?? 1),
            coutHT: Number(r.cout_ht || 0),
            prixHT: Number(r.prix_ht || 0),
            ordre: Number(r.ordre ?? i),
          })));
        }

        // Récap
        if (prest.prop_ttc != null) {
          setPropTTC(String(prest.prop_ttc));
          setPropTTCManual(true);
        }
        if (prest.prop_note) setPropNote(prest.prop_note);
      } catch (e: any) {
        toast.error(e?.message || 'Erreur chargement');
      } finally {
        setHydrating(false);
      }
    })();
  }, [isEdit, editNumero, navigate]);

  // Filtre clients
  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients.slice(0, 12);
    return clients.filter((c) =>
      [c.nom, c.prenom, c.societe, c.email, c.tel, c.ville].filter(Boolean).join(' ').toLowerCase().includes(q)
    ).slice(0, 30);
  }, [clients, clientSearch]);

  // Filtre artistes par domaine + masque les artistes déjà bookés à la date
  const filteredArtists = useMemo(() => {
    const dom = filterDomaine || (eventForm.domaines[0] ?? '');
    let list = artistesDb;
    if (dom) list = list.filter((a) => a.domaine_principal === dom || (a.domaines ?? []).includes(dom));
    if (!showBookedArtists) {
      list = list.filter((a) => !conflitsArtistes[a.id]);
    }
    // Tri : géocodés d'abord (par km), puis non-géocodés (alphabétique)
    return [...list].sort((a, b) => {
      const da = artistDistances[a.id]?.km;
      const db = artistDistances[b.id]?.km;
      if (da != null && db != null) return da - db;
      if (da != null) return -1;
      if (db != null) return 1;
      return a.nom_scene.localeCompare(b.nom_scene);
    });
  }, [artistesDb, filterDomaine, eventForm.domaines, artistDistances, conflitsArtistes, showBookedArtists]);

  // Charge les conflits d'agenda à chaque changement de date
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = await getArtistesIndisponibles(eventForm.dateEvent || null, editDbId);
      if (cancelled) return;
      const obj: Record<string, ConflitArtiste> = {};
      map.forEach((v, k) => { obj[k] = v; });
      setConflitsArtistes(obj);
    })();
    return () => { cancelled = true; };
  }, [eventForm.dateEvent, editDbId]);


  // Calcule distances artistes ↔ lieu quand on entre dans l'étape 3
  useEffect(() => {
    if (step !== 3 || !lieuCoords) return;
    const candidates = artistesDb.filter((a) => a.lat && a.lng && !artistDistances[a.id]);
    if (!candidates.length) return;
    setComputingDistances(true);
    Promise.all(
      candidates.map(async (a) => {
        try {
          const r = await calcRoute(
            { lat: Number(a.lat), lon: Number(a.lng) },
            lieuCoords
          );
          return [a.id, { km: r.km, minutes: r.minutes }] as const;
        } catch {
          return null;
        }
      })
    ).then((results) => {
      const next = { ...artistDistances };
      for (const r of results) if (r) next[r[0]] = r[1];
      setArtistDistances(next);
    }).finally(() => setComputingDistances(false));
  }, [step, lieuCoords, artistesDb]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDomaine = (d: string) => {
    setEventForm((f) => ({
      ...f,
      domaines: f.domaines.includes(d) ? f.domaines.filter((x) => x !== d) : [...f.domaines, d],
    }));
  };

  const addArtist = (a: ArtisteLite) => {
    setSelectedArtists((prev) => {
      if (prev.find((x) => x.id === a.id)) return prev;
      const dist = artistDistances[a.id];
      return [...prev, { ...a, km: dist?.km, minutes: dist?.minutes, calc: defaultCalc(a), included: true }];
    });
  };

  const toggleArtist = async (a: ArtisteLite) => {
    const exists = selectedArtists.find((x) => x.id === a.id);
    if (exists) {
      setSelectedArtists((prev) => prev.filter((x) => x.id !== a.id));
      return;
    }
    const conflit = conflitsArtistes[a.id];
    if (conflit) {
      const ok = await confirm({
        title: 'Artiste déjà booké',
        description: `${formatConflitMessage(conflit, a.nom_scene)}\n\nÊtes-vous sûr de vouloir le sélectionner malgré tout ? Cela peut créer un conflit de planning.`,
        confirmLabel: 'Forcer la sélection',
        cancelLabel: 'Annuler',
        destructive: true,
      });
      if (!ok) return;
      toast.warning(`${a.nom_scene} : conflit de planning forcé (${conflit.numero ?? 'sans n°'})`);
    }
    addArtist(a);
  };


  // Validation pour passer à l'étape suivante
  const canNext = (): boolean => {
    if (step === 1) return !!selectedClient;
    if (step === 2) return !!eventForm.dateEvent && !!lieuQuery.trim();
    if (step === 3) return true; // artistes optionnels : on peut continuer sans sélection
    return true;
  };

  // Total TTC calculé via le calculateur (étape 4) — uniquement les artistes inclus
  const totalCalcTTC = useMemo(
    () => selectedArtists.filter((a) => a.included).reduce((s, a) => s + computeArtistePrice(a.calc).venduTTC, 0),
    [selectedArtists]
  );

  // Synchronise le prix proposé TTC avec le total du calculateur (sauf si édité manuellement)
  useEffect(() => {
    if (!propTTCManual && totalCalcTTC > 0) {
      setPropTTC((Math.round(totalCalcTTC * 100) / 100).toString());
    }
  }, [totalCalcTTC, propTTCManual]);

  const goNext = async () => {
    if (!canNext()) {
      toast.error('Complétez les champs requis');
      return;
    }
    setStep((s) => Math.min(5, s + 1) as Step);
  };

  const goPrev = () => setStep((s) => Math.max(1, (s - 1)) as Step);

  const updateArtistCalc = (id: string, patch: Partial<ArtisteCalc>) => {
    setSelectedArtists((prev) => prev.map((a) => a.id === id ? { ...a, calc: { ...a.calc, ...patch } } : a));
  };

  const toggleArtistIncluded = (id: string) => {
    setSelectedArtists((prev) => prev.map((a) => a.id === id ? { ...a, included: !a.included } : a));
  };

  // Sauvegarde finale : prestation + prestations_artistes
  const buildArtistLinks = (prestId: string) => {
    // On enregistre TOUS les artistes du calculateur (même décochés) pour conserver
    // l'offre préparée ; le flag `inclus` indique s'ils entrent dans le calcul/la proposition.
    return withDemoFlagArray(selectedArtists.map((a) => {
      const p = computeArtistePrice(a.calc);
      const optsCostTotal = (a.calc.opt1On ? optionCostHT(a.calc, 1) : 0) + (a.calc.opt2On ? optionCostHT(a.calc, 2) : 0) + (a.calc.opt3On ? optionCostHT(a.calc, 3) : 0) + (a.calc.opt4On ? optionCostHT(a.calc, 4) : 0);
      const realCoutHT = a.calc.tarifHT + a.calc.fraisDepl + a.calc.vhrDepl + a.calc.vhrHotel + a.calc.vhrProd + optsCostTotal;
      const distNote = a.km != null ? `Distance ${a.km} km${a.minutes ? ` (${formatDuree(a.minutes)})` : ''}` : null;
      const optsNote = [
        a.calc.opt1Lib && a.calc.opt1On && `${a.calc.opt1Lib}: ${Math.round(optionCostHT(a.calc, 1))}€${a.calc.opt1Note ? ` (${a.calc.opt1Note})` : ''}`,
        a.calc.opt2Lib && a.calc.opt2On && `${a.calc.opt2Lib}: ${Math.round(optionCostHT(a.calc, 2))}€${a.calc.opt2Note ? ` (${a.calc.opt2Note})` : ''}`,
        a.calc.opt3Lib && a.calc.opt3On && `${a.calc.opt3Lib}: ${Math.round(optionCostHT(a.calc, 3))}€${a.calc.opt3Note ? ` (${a.calc.opt3Note})` : ''}`,
        a.calc.opt4Lib && a.calc.opt4On && `${a.calc.opt4Lib}: ${Math.round(optionCostHT(a.calc, 4))}€${a.calc.opt4Note ? ` (${a.calc.opt4Note})` : ''}`,
      ].filter(Boolean).join(' · ');
      return {
        prestation_id: prestId,
        artiste_id: a.id,
        artiste_nom: a.nom_scene,
        cout: realCoutHT,
        prop_ttc: p.venduHT,
        marge_pct: a.calc.margePct,
        km: a.km ?? null,
        duree_trajet: a.minutes ? formatDuree(a.minutes) : null,
        notes: [distNote, optsNote].filter(Boolean).join(' — ') || null,
        calc_data: a.calc as any,
        inclus: a.included !== false,
      };
    }));
  };


  const buildProduitLinks = (prestId: string) =>
    withDemoFlagArray(
      produitLignes
        .filter((l) => (l.libelle || '').trim())
        .map((l, i) => ({
          prestation_id: prestId,
          produit_id: l.produitId,
          libelle: l.libelle.trim(),
          description: l.description || null,
          quantite: Number(l.quantite) || 1,
          cout_ht: Number(l.coutHT) || 0,
          prix_ht: Number(l.prixHT) || 0,
          ordre: i,
        }))
    );

  const handleCreate = async () => {
    if (!selectedClient) return;
    setSaving(true);
    try {
      const clientFullName = [selectedClient.prenom, selectedClient.nom].filter(Boolean).join(' ').trim() || selectedClient.societe || selectedClient.nom;
      const structure = structureFilter === 'suivez_vos_reves' ? 'suivez_vos_reves' : 'cygnature';

      const commonFields = {
        structure,
        client_id: selectedClient.id,
        client_nom: clientFullName,
        client_email: selectedClient.email,
        client_tel: selectedClient.tel,
        date_event: eventForm.dateEvent,
        duree: eventForm.duree,
        lieu: lieuQuery,
        km: agencyKm,
        libelle: eventForm.libelle,
        demande: eventForm.demande,
        services: eventForm.services,
        domaines: eventForm.domaines,
        themes: eventForm.themes,
        prop_ttc: propTTC ? Number(propTTC) : null,
        prop_note: propNote || null,
        remarques: eventForm.remarques || null,
      };

      // ===== MODE ÉDITION =====
      if (isEdit && editDbId) {
        const { error: upErr } = await supabase
          .from('prestations')
          .update(commonFields as any)
          .eq('id', editDbId);
        if (upErr) throw new Error(upErr.message);

        // Recompose les liaisons artistes (delete + insert)
        await supabase.from('prestations_artistes').delete().eq('prestation_id', editDbId);
        const links = buildArtistLinks(editDbId);
        if (links.length) {
          const { error: linkErr } = await supabase.from('prestations_artistes').insert(links);
          if (linkErr) toast.warning('Prestation mise à jour mais liaison artistes partielle : ' + linkErr.message);
        }

        await (supabase.from('prestations_produits' as any) as any).delete().eq('prestation_id', editDbId);
        const prodLinks = buildProduitLinks(editDbId);
        if (prodLinks.length) {
          const { error: prodErr } = await (supabase.from('prestations_produits' as any) as any).insert(prodLinks);
          if (prodErr) toast.warning('Produits partiellement enregistrés : ' + prodErr.message);
        }

        await crmStore.reload();
        toast.success('Demande mise à jour');
        navigate('/crm-pro-evenementiel/prestations');
        return;
      }

      // ===== MODE CRÉATION =====
      const sequenceFromNumero = (value?: string | null) => {
        const match = (value || '').match(/\/(\d+)$/);
        return match ? parseInt(match[1], 10) || 0 : 0;
      };
      const baseNumero = nextPrestIdForClient(clientFullName, eventForm.dateEvent);
      const prefix = baseNumero.slice(0, baseNumero.lastIndexOf('/') + 1);
      const { data: existingRows, error: lookupError } = await supabase
        .from('prestations')
        .select('numero')
        .like('numero', `${prefix}%`);
      if (lookupError) throw new Error(lookupError.message);
      const usedNums = new Set((existingRows || []).map((r: any) => sequenceFromNumero(r.numero)).filter((n) => n > 0));
      let n = Math.max(sequenceFromNumero(baseNumero), 1);

      let prest: { id: string } | null = null;
      let duplicateError: any = null;
      while (!prest && n < 1000) {
        while (usedNums.has(n)) n++;
        const numero = `${prefix}${String(n).padStart(3, '0')}`;
        const { data, error } = await supabase
          .from('prestations')
          .insert(withDemoFlag({ numero, statut: 'Brouillon' as any, ...commonFields }) as any)
          .select('id')
          .single();
        if (!error && data) {
          prest = data;
          break;
        }
        if (error?.code === '23505' && /prestations_numero_key/.test(error.message || '')) {
          duplicateError = error;
          usedNums.add(n);
          n++;
          continue;
        }
        throw new Error(error?.message || 'Erreur prestation');
      }
      if (!prest) throw new Error(duplicateError?.message || 'Impossible de générer un numéro de prestation disponible');

      const links = buildArtistLinks(prest.id);
      if (links.length) {
        const { error: linkErr } = await supabase.from('prestations_artistes').insert(links);
        if (linkErr) toast.warning('Prestation créée mais liaison artistes partielle : ' + linkErr.message);
      }

      const prodLinks = buildProduitLinks(prest.id);
      if (prodLinks.length) {
        const { error: prodErr } = await (supabase.from('prestations_produits' as any) as any).insert(prodLinks);
        if (prodErr) toast.warning('Produits partiellement enregistrés : ' + prodErr.message);
      }

      // Note : pas d'auto-assignation. L'admin/direction assigne manuellement les intervenants
      // depuis le détail de la prestation (panneau IntervenantsPanel).

      await crmStore.reload();
      toast.success('Demande créée — direction Prestations');
      navigate('/crm-pro-evenementiel/prestations');
    } catch (e: any) {
      toast.error(e.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (hydrating) {
    return (
      <div className="min-h-[400px] flex items-center justify-center text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin mr-2" /> Chargement de la demande…
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageTitle
          title={isEdit ? `Modifier la demande${editNumero ? ` · ${editNumero}` : ''}` : 'Nouvelle demande'}
          subtitle="Client → Événement → Artistes → Calculateur → Récapitulatif"
          icon={<Sparkles className="text-amber-400" size={24} strokeWidth={1.5} />}
        />
        <button
          onClick={() => navigate('/crm-pro-evenementiel/prestations')}
          className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-sm flex items-center gap-2"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Retour aux prestations
        </button>
      </div>

      {/* Stepper — cliquable pour naviguer librement entre les étapes */}
      <Stepper step={step} onJump={(s) => setStep(s)} />


      <div className="mt-6">
        {step === 1 && (
          <Step1Client
            clients={filteredClients}
            search={clientSearch}
            onSearch={setClientSearch}
            selected={selectedClient}
            onSelect={(c) => setSelectedClient(c)}
            onClear={() => setSelectedClient(null)}
            onOpenCreate={() => { setEditingClient(null); setOpenClientForm(true); }}
            onEdit={openEditClient}
          />
        )}
        {step === 2 && (
          <Step2Event
            form={eventForm}
            onChange={setEventForm}
            lieuQuery={lieuQuery}
            onLieuChange={setLieuQuery}
            onLieuPick={(s) => {
              setLieuQuery(s.display_name);
              setLieuCoords({ lat: parseFloat(s.lat), lon: parseFloat(s.lon) });
              // Calcul agence → lieu
              calcRoute({ lat: 48.2258075, lon: 4.0406204 }, { lat: parseFloat(s.lat), lon: parseFloat(s.lon) })
                .then((r) => { setAgencyKm(r.km); setAgencyDuree(formatDuree(r.minutes)); })
                .catch(() => {});
            }}
            agencyKm={agencyKm}
            agencyDuree={agencyDuree}
            domaines={data.domaines}
            onToggleDomaine={toggleDomaine}
            services={data.services}
            onToggleService={(s) => setEventForm((f) => ({
              ...f,
              services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
            }))}
            themes={data.themes}
            onToggleTheme={(t) => setEventForm((f) => ({
              ...f,
              themes: f.themes.includes(t) ? f.themes.filter((x) => x !== t) : [...f.themes, t],
            }))}
          />
        )}
        {step === 3 && (
          <Step3Artists
            artists={filteredArtists}
            distances={artistDistances}
            selected={selectedArtists}
            onToggle={toggleArtist}
            domaines={data.domaines}
            filterDomaine={filterDomaine}
            onFilterDomaine={setFilterDomaine}
            computing={computingDistances}
            hasLieu={!!lieuCoords}
            conflits={conflitsArtistes}
            showBooked={showBookedArtists}
            onToggleShowBooked={setShowBookedArtists}
            dateEvent={eventForm.dateEvent}
          />
        )}
        {step === 4 && (
          <Step4Calculateur
            artists={selectedArtists}
            onUpdate={updateArtistCalc}
            onToggleIncluded={toggleArtistIncluded}
            allArtistes={artistesDb}
            artistDistances={artistDistances}
            onAddArtist={addArtist}
            produitLignes={produitLignes}
            onChangeProduits={setProduitLignes}
          />
        )}
        {step === 5 && (
          <>
            <Step5Recap
              client={selectedClient!}
              eventForm={eventForm}
              lieu={lieuQuery}
              agencyKm={agencyKm}
              agencyDuree={agencyDuree}
              artists={selectedArtists}
              propTTC={propTTC}
              onChangePropTTC={(v) => { setPropTTC(v); setPropTTCManual(true); }}
              propNote={propNote}
              onChangePropNote={setPropNote}
              totalCalcTTC={totalCalcTTC}
            />
            <div className="mt-4">
              {isEdit && editNumero ? (
                <IntervenantsPanel prestNumero={editNumero} />
              ) : (
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-xs text-slate-400 italic">
                  Attribution des intervenants disponible après la création du dossier — utilise le bouton « Intervenants » dans la liste des prestations.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer nav */}
      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200"
        >
          Annuler
        </button>
        <div className="flex items-center gap-2">
          {step > 1 && (
            <button
              onClick={goPrev}
              className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-sm flex items-center gap-2"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> Précédent
            </button>
          )}
          {step < 5 && (
            <button
              onClick={goNext}
              disabled={!canNext()}
              className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-950 font-medium rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              Suivant <ArrowRight size={14} strokeWidth={2} />
            </button>
          )}
          {step === 5 && (
            <button
              onClick={handleCreate}
              disabled={saving}
              className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-medium rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2} />}
              {isEdit ? 'Enregistrer les modifications' : 'Créer la demande'}
            </button>
          )}
        </div>
      </div>

      {/* Modal de création client (formulaire complet, identique à la page Clients) */}
      <ClientForm
        open={openClientForm}
        onOpenChange={(v) => { setOpenClientForm(v); if (!v) setEditingClient(null); }}
        initial={editingClient}
        onSaved={handleClientSaved}
      />
    </div>
  );
}

/* =========================================================
   STEPPER
========================================================= */
function Stepper({ step, onJump }: { step: Step; onJump?: (s: Step) => void }) {
  const steps = [
    { n: 1, label: 'Client', icon: User },
    { n: 2, label: 'Événement', icon: Calendar },
    { n: 3, label: 'Artistes', icon: Mic2 },
    { n: 4, label: 'Calculateur', icon: Calculator },
    { n: 5, label: 'Récapitulatif', icon: FileText },
  ] as const;
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const active = step === s.n;
        const done = step > s.n;
        return (
          <div key={s.n} className="flex items-center gap-2 flex-1">
            <button
              type="button"
              onClick={() => onJump?.(s.n as Step)}
              title={`Aller à : ${s.label}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg flex-1 transition-colors text-left cursor-pointer hover:brightness-110 ${
                active ? 'bg-amber-500/15 border border-amber-500/40 text-amber-300'
                : done ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300'
                : 'bg-slate-900/40 border border-slate-800 text-slate-500 hover:border-slate-700'
              }`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                active ? 'bg-amber-500 text-slate-950' : done ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800'
              }`}>
                {done ? <Check size={13} strokeWidth={2.5} /> : s.n}
              </span>
              <Icon size={14} strokeWidth={1.5} />
              <span className="text-xs font-medium hidden md:inline">{s.label}</span>
            </button>
            {i < steps.length - 1 && (
              <div className={`h-px flex-shrink w-4 ${done ? 'bg-emerald-500/40' : 'bg-slate-800'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* =========================================================
   STEP 1 — CLIENT
========================================================= */
function Step1Client({
  clients, search, onSearch, selected, onSelect, onClear, onOpenCreate, onEdit,
}: {
  clients: ClientLite[];
  search: string;
  onSearch: (v: string) => void;
  selected: ClientLite | null;
  onSelect: (c: ClientLite) => void;
  onClear: () => void;
  onOpenCreate: () => void;
  onEdit: () => void;
}) {
  const fullName = selected ? ([selected.prenom, selected.nom].filter(Boolean).join(' ') || selected.societe || selected.nom) : '';
  const addrLine = selected ? [normalizeAdresse(selected.adresse, selected.cp, selected.ville), selected.pays].filter(Boolean).join(', ') : '';

  return (
    <Card className="p-6">
      <h2 className="text-lg font-heading text-slate-200 mb-1">1. Sélectionnez ou créez un client</h2>
      <p className="text-xs text-slate-500 mb-5">
        Recherchez parmi vos clients existants, ou créez-en un nouveau (formulaire complet, identique à la page Clients).
      </p>

      {/* Fiche client sélectionné */}
      {selected && (
        <div className="mb-5 p-4 rounded-lg border border-amber-500/40 bg-amber-500/5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">Client sélectionné</div>
              <div className="text-base font-heading text-slate-100">{fullName}</div>
              {selected.societe && [selected.prenom, selected.nom].filter(Boolean).length > 0 && (
                <div className="text-xs text-slate-400">{selected.societe}{selected.fonction ? ` · ${selected.fonction}` : ''}</div>
              )}
              {selected.typeClient && (
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{selected.typeClient}</div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={onEdit}
                className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700"
                title="Modifier la fiche complète"
              >
                <Sparkles size={12} strokeWidth={1.5} /> Voir / modifier la fiche
              </button>
              <button
                onClick={onClear}
                className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/40"
                title="Retirer le client de la sélection"
              >
                <X size={12} strokeWidth={2} /> Retirer
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
            <FieldLine label="Email" value={selected.email} />
            <FieldLine label="Téléphone" value={selected.tel} />
            <FieldLine label="Tél. secondaire" value={selected.telSecondaire} />
            <FieldLine label="Adresse" value={addrLine || null} />
            <FieldLine label="SIRET" value={selected.siret} />
            <FieldLine label="TVA intra" value={selected.tvaIntra} />
            <FieldLine label="Segment" value={selected.segment} />
            <FieldLine label="Source" value={selected.source} />
          </div>
          {selected.notes && (
            <div className="mt-3 pt-3 border-t border-amber-500/20">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Notes</div>
              <div className="text-xs text-slate-300 whitespace-pre-wrap">{selected.notes}</div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Rechercher un client (nom, société, email…)"
            className={inputCls + ' pl-9'}
          />
        </div>
        <button
          onClick={onOpenCreate}
          className="px-3 py-2 rounded-lg text-xs flex items-center gap-2 bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25"
        >
          <Plus size={13} strokeWidth={2} />
          Nouveau client
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
        {clients.map((c) => {
          const isSel = selected?.id === c.id;
          const display = [c.prenom, c.nom].filter(Boolean).join(' ') || c.societe || c.nom;
          return (
            <button
              key={c.id}
              onClick={() => onSelect(c)}
              className={`text-left p-3 rounded-lg border transition-colors ${
                isSel ? 'bg-amber-500/10 border-amber-500/40' : 'bg-slate-900/40 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm text-slate-100 font-medium truncate">{display}</div>
                {isSel && <Check size={14} className="text-amber-400 shrink-0" strokeWidth={2} />}
              </div>
              {c.societe && [c.prenom, c.nom].filter(Boolean).length > 0 && (
                <div className="text-[11px] text-slate-400 truncate">{c.societe}</div>
              )}
              <div className="text-[11px] text-slate-500 truncate mt-0.5">
                {[c.email, c.tel, c.ville].filter(Boolean).join(' · ') || '—'}
              </div>
            </button>
          );
        })}
        {!clients.length && (
          <div className="col-span-2 text-center py-12 text-slate-500 text-sm">
            Aucun client trouvé. Créez-en un nouveau.
          </div>
        )}
      </div>
    </Card>
  );
}

function FieldLine({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 shrink-0">{label} :</span>
      <span className="text-slate-200 truncate">{value}</span>
    </div>
  );
}

/* =========================================================
   STEP 2 — ÉVÉNEMENT
========================================================= */
function Step2Event({
  form, onChange, lieuQuery, onLieuChange, onLieuPick, agencyKm, agencyDuree,
  domaines, onToggleDomaine, services, onToggleService, themes, onToggleTheme,
}: {
  form: any; onChange: (v: any) => void;
  lieuQuery: string; onLieuChange: (v: string) => void; onLieuPick: (s: NominatimSuggestion) => void;
  agencyKm: number | null; agencyDuree: string;
  domaines: string[]; onToggleDomaine: (d: string) => void;
  services: string[]; onToggleService: (s: string) => void;
  themes: string[]; onToggleTheme: (t: string) => void;
}) {
  return (
    <Card className="p-6">
      <h2 className="text-lg font-heading text-slate-200 mb-1">2. Détails de l'événement</h2>
      <p className="text-xs text-slate-500 mb-5">Date, lieu, type et domaines artistiques recherchés.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Date événement *">
          <input type="date" value={form.dateEvent} onChange={(e) => onChange({ ...form, dateEvent: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Durée">
          <input value={form.duree} onChange={(e) => onChange({ ...form, duree: e.target.value })} className={inputCls} placeholder="4h" />
        </Field>
        <Field label="Source de la demande">
          <select value={form.demande} onChange={(e) => onChange({ ...form, demande: e.target.value })} className={inputCls}>
            {['Site Web', 'Téléphone', 'Email', 'Recommandation', 'Réseaux sociaux', 'Salon', 'Autre'].map((l) => <option key={l}>{l}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Lieu de la prestation *">
            <LieuAutocomplete value={lieuQuery} onChange={onLieuChange} onPick={onLieuPick} hasCoords={agencyKm != null} />
          </Field>
          {agencyKm != null && (
            <div className="mt-1.5 text-[11px] text-emerald-400 flex items-center gap-1.5">
              <Map size={12} strokeWidth={1.5} />
              <span>Depuis l'agence : <strong>{agencyKm} km</strong>{agencyDuree && ` · ${agencyDuree}`}</span>
            </div>
          )}
        </div>
      </div>

      {/* Thèmes événement */}
      {themes.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Type d'événement (ex&nbsp;: Arbre de Noël, Mariage…)</div>
          <div className="flex flex-wrap gap-2">
            {themes.map((t) => {
              const sel = (form.themes || []).includes(t);
              return (
                <button
                  key={t}
                  onClick={() => onToggleTheme(t)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    sel ? 'bg-pink-500/15 border-pink-500/40 text-pink-300' : 'bg-slate-900/40 border-slate-800 text-slate-400 hover:border-slate-700'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Services */}
      {services.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Services</div>
          <div className="flex flex-wrap gap-2">
            {services.map((s) => {
              const sel = (form.services || []).includes(s);
              return (
                <button
                  key={s}
                  onClick={() => onToggleService(s)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    sel ? 'bg-sky-500/15 border-sky-500/40 text-sky-300' : 'bg-slate-900/40 border-slate-800 text-slate-400 hover:border-slate-700'
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Domaines artistiques */}
      <div className="mt-5">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Domaines artistiques recherchés</div>
        <div className="flex flex-wrap gap-2">
          {domaines.map((d) => {
            const sel = form.domaines.includes(d);
            return (
              <button
                key={d}
                onClick={() => onToggleDomaine(d)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  sel ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-slate-900/40 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <Field label="Remarques / brief client">
          <textarea
            value={form.remarques}
            onChange={(e) => onChange({ ...form, remarques: e.target.value })}
            rows={3}
            className={inputCls}
            placeholder="Ambiance souhaitée, contraintes, public…"
          />
        </Field>
      </div>
    </Card>
  );
}

/* =========================================================
   STEP 3 — ARTISTES
========================================================= */
function Step3Artists({
  artists, distances, selected, onToggle, domaines, filterDomaine, onFilterDomaine, computing, hasLieu,
  conflits, showBooked, onToggleShowBooked, dateEvent,
}: {
  artists: ArtisteLite[];
  distances: Record<string, { km: number; minutes: number }>;
  selected: SelectedArtist[];
  onToggle: (a: ArtisteLite) => void;
  domaines: string[];
  filterDomaine: string;
  onFilterDomaine: (v: string) => void;
  computing: boolean;
  hasLieu: boolean;
  conflits: Record<string, ConflitArtiste>;
  showBooked: boolean;
  onToggleShowBooked: (v: boolean) => void;
  dateEvent: string;
}) {
  const nbConflits = Object.keys(conflits).length;
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h2 className="text-lg font-heading text-slate-200 mb-1">3. Sélectionnez les artistes</h2>
          <p className="text-xs text-slate-500">
            {hasLieu ? 'Triés par proximité avec le lieu de la prestation.' : 'Définissez d\'abord un lieu pour le tri par distance.'}
            {computing && <span className="ml-2 text-amber-400 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Calcul des distances…</span>}
          </p>
          {dateEvent && nbConflits > 0 && (
            <p className="text-[11px] text-rose-400 mt-1">
              {nbConflits} artiste{nbConflits > 1 ? 's' : ''} déjà booké{nbConflits > 1 ? 's' : ''} le {new Date(dateEvent).toLocaleDateString('fr-FR')} {showBooked ? '(affichés grisés)' : '(masqués)'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {nbConflits > 0 && (
            <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showBooked}
                onChange={(e) => onToggleShowBooked(e.target.checked)}
                className="accent-amber-500"
              />
              Afficher les bookés
            </label>
          )}
          <select value={filterDomaine} onChange={(e) => onFilterDomaine(e.target.value)} className={inputCls + ' min-w-[180px]'}>
            <option value="">Tous les domaines</option>
            {domaines.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[480px] overflow-y-auto">
        {artists.map((a) => {
          const isSel = !!selected.find((x) => x.id === a.id);
          const dist = distances[a.id];
          const conflit = conflits[a.id];
          return (
            <button
              key={a.id}
              onClick={() => onToggle(a)}
              className={`text-left p-3 rounded-lg border transition-colors ${
                isSel ? 'bg-amber-500/10 border-amber-500/40'
                : conflit ? 'bg-rose-950/20 border-rose-900/40 hover:border-rose-800/60 opacity-70'
                : 'bg-slate-900/40 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className={`text-sm font-medium truncate ${conflit && !isSel ? 'text-rose-300' : 'text-amber-300'}`}>{a.nom_scene}</div>
                {isSel && <Check size={14} className="text-amber-400 shrink-0" strokeWidth={2} />}
                {conflit && !isSel && (
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded shrink-0">Booké</span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400 flex-wrap">
                {(() => {
                  const allDoms = Array.from(new Set([
                    ...(a.domaine_principal ? [a.domaine_principal] : []),
                    ...((a.domaines ?? []) as string[]),
                  ]));
                  return allDoms.length > 0 ? <span>{allDoms.join(' · ')}</span> : null;
                })()}
                {a.ville && <span className="flex items-center gap-1"><MapPin size={10} strokeWidth={1.5} /> {a.ville}</span>}
                {dist && (
                  <span className="text-emerald-400">
                    {dist.km} km · {formatDuree(dist.minutes)}
                  </span>
                )}
                {!dist && a.lat && a.lng && hasLieu && (
                  <span className="text-slate-500 italic">calcul…</span>
                )}
                {!a.lat && (
                  <span className="text-slate-600 italic">non géocodé</span>
                )}
              </div>
              {conflit && (
                <div className="text-[10px] text-rose-400/90 mt-1">
                  Conflit : {conflit.numero ?? 'prestation'} · {conflit.clientNom ?? '—'} · {conflit.statut}
                </div>
              )}
              <div className="text-[11px] text-slate-500 mt-0.5">
                {a.tarif_base != null && <span>Tarif base : {a.tarif_base} € HT</span>}
              </div>
            </button>
          );
        })}
        {!artists.length && (
          <div className="col-span-2 text-center py-12 text-slate-500 text-sm">
            Aucun artiste correspondant.
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <div className="text-[10px] text-amber-400 uppercase tracking-widest mb-2">{selected.length} artiste{selected.length > 1 ? 's' : ''} sélectionné{selected.length > 1 ? 's' : ''}</div>
          <div className="flex flex-wrap gap-1.5">
            {selected.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-amber-500/15 text-amber-200 rounded">
                {a.nom_scene}
                <button onClick={() => onToggle(a)}><X size={10} strokeWidth={2} /></button>
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/* =========================================================
   STEP 4 — RÉCAPITULATIF
========================================================= */
/* =========================================================
   STEP 4 — CALCULATEUR (par artiste)
========================================================= */
function Step4Calculateur({
  artists, onUpdate, onToggleIncluded, allArtistes, artistDistances, onAddArtist,
  produitLignes, onChangeProduits,
}: {
  artists: SelectedArtist[];
  onUpdate: (id: string, patch: Partial<ArtisteCalc>) => void;
  onToggleIncluded: (id: string) => void;
  allArtistes: ArtisteLite[];
  artistDistances: Record<string, { km: number; minutes: number }>;
  onAddArtist: (a: ArtisteLite) => void;
  produitLignes: ProduitLigne[];
  onChangeProduits: (next: ProduitLigne[]) => void;
}) {
  const included = artists.filter((a) => a.included);
  const prodT = produitsTotals(produitLignes);
  const totalCout = included.reduce((s, a) => s + computeArtistePrice(a.calc).coutHT, 0) + prodT.coutHT;
  const totalVenduHT = included.reduce((s, a) => s + computeArtistePrice(a.calc).venduHT, 0) + prodT.venduHT;
  const totalVenduTTC = included.reduce((s, a) => s + computeArtistePrice(a.calc).venduTTC, 0) + prodT.venduHT * 1.2;

  const [search, setSearch] = useState('');
  const selectedIds = new Set(artists.map((a) => a.id));
  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allArtistes
      .filter((a) => !selectedIds.has(a.id))
      .filter((a) => {
        const hay = `${a.nom_scene} ${a.domaine_principal || ''} ${(a.domaines || []).join(' ')} ${a.ville || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
  }, [search, allArtistes, artists]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start">
      <div className="space-y-4 min-w-0">
        <Card className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calculator size={16} className="text-amber-400" strokeWidth={1.5} />
              <span className="text-slate-300 font-medium">Calculateur de prestation</span>
            </div>
            <div className="text-slate-500 text-xs flex-1">
              Décochez un artiste pour l'exclure du calcul et de la proposition (sans le retirer).
            </div>
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2">
              <Search size={14} className="text-slate-500" strokeWidth={1.5} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un artiste (nom, domaine, ville)…"
                className="flex-1 bg-transparent outline-none text-sm text-slate-200 placeholder:text-slate-600"
              />
              {search && (
                <button onClick={() => setSearch('')} className="text-slate-500 hover:text-slate-300">
                  <X size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
            {suggestions.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-md shadow-xl max-h-72 overflow-auto">
                {suggestions.map((a) => {
                  const dist = artistDistances[a.id];
                  return (
                    <button
                      key={a.id}
                      onClick={() => { onAddArtist(a); setSearch(''); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-800 flex items-center gap-2 border-b border-slate-800/60 last:border-0"
                    >
                      <Plus size={12} className="text-amber-400 shrink-0" strokeWidth={2} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">{a.nom_scene}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {a.domaine_principal || '—'}
                          {a.ville ? ` · ${a.ville}` : ''}
                          {dist ? ` · ${Math.round(dist.km)} km` : ''}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {search.trim() && suggestions.length === 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-500 italic">
                Aucun artiste trouvé.
              </div>
            )}
          </div>
        </Card>

        {artists.length === 0 && (
          <Card className="p-8 text-center text-slate-400 text-sm">
            Aucun artiste sélectionné. Utilisez la recherche ci-dessus pour en ajouter, ou revenez à l'étape précédente.
          </Card>
        )}

        {artists.map((a) => (
          <ArtisteCalcCard
            key={a.id}
            artist={a}
            onUpdate={(patch) => onUpdate(a.id, patch)}
            onToggleIncluded={() => onToggleIncluded(a.id)}
          />
        ))}

        <ProduitsPicker lignes={produitLignes} onChange={onChangeProduits} />

        <Card className="p-5 bg-amber-500/5 border-amber-500/30">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">
            Total ({included.length}/{artists.length} artiste{artists.length > 1 ? 's' : ''} inclus{produitLignes.length ? ` + ${produitLignes.length} produit${produitLignes.length > 1 ? 's' : ''}` : ''})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Coût total HT</div>
              <div className="text-slate-200 text-lg">{Math.round(totalCout)} €</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Vendu HT</div>
              <div className="text-slate-200 text-lg">{Math.round(totalVenduHT)} €</div>
            </div>
            <div>
              <div className="text-[10px] text-emerald-400 uppercase tracking-widest mb-1">Marge totale</div>
              <div className="text-emerald-400 text-lg font-medium">{Math.round(totalVenduHT - totalCout)} €</div>
              <div className="text-[10px] text-slate-500">{totalCout > 0 ? Math.round(((totalVenduHT - totalCout) / totalCout) * 100) : 0}%</div>
            </div>
            <div>
              <div className="text-[10px] text-amber-300 uppercase tracking-widest mb-1">Vendu TTC</div>
              <div className="text-amber-300 text-xl font-medium">{(Math.round((totalVenduTTC) * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €</div>
            </div>
          </div>
        </Card>
      </div>

      <DetailPanel artists={included} />
    </div>
  );
}

function DetailPanel({ artists }: { artists: SelectedArtist[] }) {
  const fmt = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`;
  const totals = artists.reduce(
    (acc, a) => {
      const p = computeArtistePrice(a.calc);
      const tva = p.venduTTC - p.venduHT;
      acc.cout += p.coutHT;
      acc.venduHT += p.venduHT;
      acc.tva += tva;
      acc.ttc += p.venduTTC;
      acc.marge += p.venduHT - p.coutHT;
      return acc;
    },
    { cout: 0, venduHT: 0, tva: 0, ttc: 0, marge: 0 },
  );

  return (
    <Card className="p-4 xl:sticky xl:top-4 bg-slate-950/40">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={14} className="text-amber-400" strokeWidth={1.5} />
        <span className="text-[10px] text-slate-400 uppercase tracking-widest">Détail du calcul</span>
      </div>

      {artists.length === 0 && (
        <div className="text-xs text-slate-500 italic">Aucun artiste inclus.</div>
      )}

      <div className="space-y-4">
        {artists.map((a) => {
          const c = a.calc;
          const p = computeArtistePrice(c);
          const tva = p.venduTTC - p.venduHT;
          const optsCostTotal = (c.opt1On ? optionCostHT(c, 1) : 0) + (c.opt2On ? optionCostHT(c, 2) : 0) + (c.opt3On ? optionCostHT(c, 3) : 0) + (c.opt4On ? optionCostHT(c, 4) : 0);
          const coutReel = c.tarifHT + c.fraisDepl + c.vhrDepl + c.vhrHotel + c.vhrProd + optsCostTotal;
          const margeEuros = p.venduHT - coutReel;
          const margeReellePct = coutReel > 0 ? (margeEuros / coutReel) * 100 : 0;
          return (
            <div key={a.id} className="text-[11px] space-y-1 pb-3 border-b border-slate-800 last:border-0">
              <div className="text-amber-200 font-medium text-xs mb-1.5">{a.nom_scene}</div>
              <Row label="Tarif HT" value={fmt(c.tarifHT)} />
              {c.fraisDepl > 0 && <Row label="Frais déplacement" value={fmt(c.fraisDepl)} />}
              {c.vhrDepl > 0 && <Row label="VHR · Déplacement" value={fmt(c.vhrDepl)} sub />}
              {c.vhrHotel > 0 && <Row label="VHR · Hôtel" value={fmt(c.vhrHotel)} sub />}
              {c.vhrProd > 0 && <Row label="VHR · Production" value={fmt(c.vhrProd)} sub />}
              {[1, 2, 3, 4].map((n) => {
                const lib = (c as any)[`opt${n}Lib`] as string;
                const on = (c as any)[`opt${n}On`] as boolean;
                if (!on) return null;
                const ht = optionCostHT(c, n as 1 | 2 | 3 | 4);
                if (!ht) return null;
                return <Row key={n} label={lib || `Option ${n}`} value={fmt(ht)} sub />;
              })}
              <div className="flex justify-between pt-1 border-t border-slate-800/60">
                <span className="text-slate-400">= Coût HT</span>
                <span className="text-slate-200">{fmt(coutReel)}</span>
              </div>
              <Row label={`Marge (${margeReellePct.toFixed(1)}%)`} value={fmt(margeEuros)} accent="emerald" />
              <div className="flex justify-between pt-1 border-t border-slate-800/60">
                <span className="text-slate-400">Vendu HT</span>
                <span className="text-slate-200">{fmt(p.venduHT)}</span>
              </div>
              <Row label={`TVA (${c.tvaPct || 0}%)`} value={fmt(tva)} />
              <div className="flex justify-between pt-1 border-t border-slate-800/60">
                <span className="text-amber-300">Vendu TTC</span>
                <span className="text-amber-300 font-medium">{fmt(p.venduTTC)}</span>
              </div>
            </div>
          );
        })}
      </div>


      {artists.length > 1 && (() => {
        const margePct = totals.cout > 0 ? Math.round((totals.marge / totals.cout) * 100) : 0;
        const tvaPct = totals.venduHT > 0 ? Math.round((totals.tva / totals.venduHT) * 1000) / 10 : 0;
        return (
          <div className="mt-3 pt-3 border-t border-amber-500/30 space-y-1 text-[11px]">
            <div className="text-[10px] text-amber-300 uppercase tracking-widest mb-1.5">Totaux</div>
            <Row label="Coût HT total" value={fmt(totals.cout)} />
            <Row label={`Marge totale (${margePct}%)`} value={fmt(totals.marge)} accent="emerald" />
            <Row label="Vendu HT total" value={fmt(totals.venduHT)} />
            <Row label={`TVA totale (${tvaPct}%)`} value={fmt(totals.tva)} />
            <div className="flex justify-between pt-1 border-t border-slate-800/60">
              <span className="text-amber-300">Vendu TTC total</span>
              <span className="text-amber-300 font-medium text-sm">{fmt(totals.ttc)}</span>
            </div>
          </div>
        );
      })()}
    </Card>
  );
}

function Row({ label, value, sub, accent }: { label: string; value: string; sub?: boolean; accent?: 'emerald' }) {
  return (
    <div className="flex justify-between">
      <span className={sub ? 'text-slate-500 pl-2' : 'text-slate-500'}>{label}</span>
      <span className={accent === 'emerald' ? 'text-emerald-400' : 'text-slate-300'}>{value}</span>
    </div>
  );
}

function ArtisteCalcCard({ artist, onUpdate, onToggleIncluded }: { artist: SelectedArtist; onUpdate: (patch: Partial<ArtisteCalc>) => void; onToggleIncluded: () => void }) {
  const c = artist.calc;
  const p = computeArtistePrice(c);
  const num = (v: string) => Number(v) || 0;
  const inc = artist.included;

  return (
    <Card className={`p-5 transition-opacity ${inc ? '' : 'opacity-50'}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <label className="flex items-center gap-2 cursor-pointer mt-1">
            <input
              type="checkbox"
              checked={inc}
              onChange={onToggleIncluded}
              className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-amber-500 focus:ring-amber-500/40"
            />
          </label>
          <div>
            <div className="text-base font-heading text-amber-200">{artist.nom_scene}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {artist.domaine_principal || '—'}
              {artist.km != null && <> · {artist.km} km{artist.minutes ? ` (${formatDuree(artist.minutes)})` : ''}</>}
              {!inc && <> · <span className="text-slate-500 italic">exclu du calcul (offre conservée)</span></>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest">Vendu HT {p.isLibre && <span className="text-amber-400">· libre</span>}</div>
          <div className="text-amber-300 font-medium text-lg">{Math.round(p.venduHT)} €</div>
          <div className="text-[10px] text-slate-500">{(Math.round(p.venduTTC * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}€ TTC · coût {(Math.round(p.coutHT * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}€ HT</div>
        </div>
      </div>

      <div className="mb-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className="text-[10px] text-amber-300 uppercase tracking-widest mb-1.5">Prix libre HT — override (€)</div>
            <div className="flex gap-2">
              <input
                type="number"
                value={c.prixLibreHT && c.prixLibreHT > 0 ? c.prixLibreHT : ''}
                onFocus={(e) => e.target.select()}
                onChange={(e) => onUpdate({ prixLibreHT: num(e.target.value) })}
                placeholder={`Auto : ${Math.round(p.venduHTAuto)} €`}
                className={inputCls}
              />
              <button
                type="button"
                onClick={() => onUpdate({ prixLibreHT: 0 })}
                className="px-3 py-2 text-xs rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 whitespace-nowrap"
                title="Revenir au calcul automatique"
              >
                ↻ Auto
              </button>
            </div>
            <span className="text-[10px] text-slate-500 mt-1 block">Saisissez un Vendu HT libre pour cet artiste. Vide ou « ↻ Auto » = calcul automatique (tarif + marge).</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CalcField label="Tarif HT (€)" value={c.tarifHT} onChange={(v) => onUpdate({ tarifHT: num(v) })} />
        <CalcField label="Marge (%)" value={c.margePct} onChange={(v) => onUpdate({ margePct: num(v) })} />
        <CalcField label="Frais déplacement (€)" value={c.fraisDepl} onChange={(v) => onUpdate({ fraisDepl: num(v) })} />
        <label className="block">
          <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">TVA</span>
          <TvaSelect value={c.tvaPct} onChange={(v) => onUpdate({ tvaPct: v })} />
        </label>
      </div>

      <div className="mt-4">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Frais VHR (Voyage / Hébergement / Restauration)</div>
        <div className="grid grid-cols-3 gap-3">
          <CalcField label="Déplacement (€)" value={c.vhrDepl} onChange={(v) => onUpdate({ vhrDepl: num(v) })} />
          <CalcField label="Hôtel (€)" value={c.vhrHotel} onChange={(v) => onUpdate({ vhrHotel: num(v) })} />
          <CalcField label="Production (€)" value={c.vhrProd} onChange={(v) => onUpdate({ vhrProd: num(v) })} />
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Options additionnelles</div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((n) => {
            const lib = (c as any)[`opt${n}Lib`] as string;
            const ht = (c as any)[`opt${n}HT`] as number;
            const on = (c as any)[`opt${n}On`] as boolean;
            const tarif = ((c as any)[`opt${n}Tarif`] as number) || 0;
            const marge = ((c as any)[`opt${n}Marge`] as number) || 0;
            const note = ((c as any)[`opt${n}Note`] as string) || '';
            const auto = tarif * (1 + marge / 100);
            const finalHT = ht > 0 ? ht : auto;
            return (
              <div key={n} className={`p-2.5 rounded-lg border ${on ? 'border-slate-800 bg-slate-900/30' : 'border-slate-800/50 opacity-50'}`}>
                <div className="grid grid-cols-[auto_1fr_140px] gap-2 items-center">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => onUpdate({ [`opt${n}On`]: e.target.checked } as any)}
                    className="h-4 w-4 accent-amber-500"
                    title={on ? 'Inclure dans le calcul' : 'Exclu du calcul'}
                  />
                  <input
                    value={lib}
                    onChange={(e) => onUpdate({ [`opt${n}Lib`]: e.target.value } as any)}
                    placeholder={`Option ${n} (libellé)`}
                    className={inputCls}
                  />
                  <div className="text-right">
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">HT final</div>
                    <div className="text-amber-300 text-sm font-medium">{Math.round(finalHT)} €</div>
                  </div>
                </div>
                {on && (
                  <div className="grid grid-cols-3 gap-2 mt-2 pl-6">
                    <CalcField label="Tarif HT artiste (€)" value={tarif} onChange={(v) => onUpdate({ [`opt${n}Tarif`]: num(v) } as any)} />
                    <CalcField label="Marge (%)" value={marge} onChange={(v) => onUpdate({ [`opt${n}Marge`]: num(v) } as any)} />
                    <label className="block">
                      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Prix libre HT (override)</span>
                      <input
                        type="number"
                        value={ht === 0 ? '' : ht}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => onUpdate({ [`opt${n}HT`]: num(e.target.value) } as any)}
                        placeholder={`Auto : ${Math.round(auto)} €`}
                        className={inputCls}
                      />
                    </label>
                    <div className="col-span-3">
                      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Zone libre / note</span>
                      <input
                        value={note}
                        onChange={(e) => onUpdate({ [`opt${n}Note`]: e.target.value } as any)}
                        placeholder="Précisions, conditions, détails…"
                        className={inputCls}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-800 space-y-1 text-xs">
        <div className="flex justify-between"><span className="text-slate-500">Tarif HT</span><span className="text-slate-300">{Math.round(c.tarifHT)} €</span></div>
        {c.fraisDepl > 0 && <div className="flex justify-between"><span className="text-slate-500">+ Frais déplacement</span><span className="text-slate-300">{Math.round(c.fraisDepl)} €</span></div>}
        {(c.vhrDepl + c.vhrHotel + c.vhrProd) > 0 && <div className="flex justify-between"><span className="text-slate-500">+ VHR</span><span className="text-slate-300">{Math.round(c.vhrDepl + c.vhrHotel + c.vhrProd)} €</span></div>}
        {((c.opt1On ? optionCostHT(c, 1) : 0) + (c.opt2On ? optionCostHT(c, 2) : 0) + (c.opt3On ? optionCostHT(c, 3) : 0) + (c.opt4On ? optionCostHT(c, 4) : 0)) > 0 && <div className="flex justify-between"><span className="text-slate-500">+ Options</span><span className="text-slate-300">{Math.round((c.opt1On ? optionCostHT(c, 1) : 0) + (c.opt2On ? optionCostHT(c, 2) : 0) + (c.opt3On ? optionCostHT(c, 3) : 0) + (c.opt4On ? optionCostHT(c, 4) : 0))} €</span></div>}
        <div className="flex justify-between pt-1 border-t border-slate-800/60"><span className="text-slate-400">= Coût HT</span><span className="text-slate-200">{(Math.round((p.coutHT) * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €</span></div>
        {(() => {
          const margeReellePct = p.coutHT > 0 ? ((p.venduHT - p.coutHT) / p.coutHT) * 100 : 0;
          return (
            <>
              <div className="flex justify-between"><span className="text-slate-500">× (1 + {Math.round(margeReellePct)}% marge)</span><span className="text-slate-300">{Math.round(p.venduHT)} € HT</span></div>
              <div className="flex justify-between"><span className="text-slate-500">× (1 + {c.tvaPct || 0}% TVA)</span><span className="text-amber-300 font-medium">{(Math.round((p.venduTTC) * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} € TTC</span></div>
              <div className="flex justify-between pt-1 border-t border-slate-800/60"><span className="text-slate-400">Marge dégagée</span><span className="text-emerald-400">{Math.round(p.venduHT - p.coutHT)} € ({margeReellePct.toFixed(1)}%)</span></div>
            </>
          );
        })()}
      </div>
    </Card>
  );
}

function CalcField({ label, value, onChange }: { label: string; value: number; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">{label}</span>
      <input
        type="number"
        value={value === 0 ? '' : value}
        placeholder="0"
        onFocus={(e) => e.target.select()}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
      />
    </label>
  );
}

/* =========================================================
   STEP 5 — RÉCAPITULATIF
========================================================= */
function Step5Recap({
  client, eventForm, lieu, agencyKm, agencyDuree, artists, propTTC, onChangePropTTC, propNote, onChangePropNote, totalCalcTTC,
}: {
  client: ClientLite;
  eventForm: any;
  lieu: string;
  agencyKm: number | null;
  agencyDuree: string;
  artists: SelectedArtist[];
  propTTC: string;
  onChangePropTTC: (v: string) => void;
  propNote: string;
  onChangePropNote: (v: string) => void;
  totalCalcTTC: number;
}) {
  const includedArtists = artists.filter((a) => a.included);
  const totalCout = includedArtists.reduce((s, a) => s + computeArtistePrice(a.calc).coutHT, 0);
  const clientName = [client?.prenom, client?.nom].filter(Boolean).join(' ') || client?.societe || '—';

  return (
    <div className="grid grid-cols-1 gap-5">
      <Card className="p-6">
        <h2 className="text-lg font-heading text-slate-200 mb-4">5. Récapitulatif</h2>


        <Section icon={User} title="Client">
          <RecapRow label="Nom" value={clientName} />
          {client?.societe && <RecapRow label="Société" value={client.societe} />}
          <RecapRow label="Email" value={client?.email || '—'} />
          <RecapRow label="Téléphone" value={client?.tel || '—'} />
        </Section>

        <Section icon={Calendar} title="Événement">
          <RecapRow label="Type" value={(eventForm.themes || []).join(', ') || eventForm.libelle} />
          <RecapRow label="Date" value={eventForm.dateEvent} />
          <RecapRow label="Durée" value={eventForm.duree} />
          <RecapRow label="Lieu" value={lieu} />
          {agencyKm != null && (
            <RecapRow label="Distance agence" value={`${agencyKm} km${agencyDuree ? ` · ${agencyDuree}` : ''}`} />
          )}
          {eventForm.domaines.length > 0 && (
            <RecapRow label="Domaines" value={eventForm.domaines.join(', ')} />
          )}
          {eventForm.remarques && (
            <RecapRow label="Remarques" value={eventForm.remarques} />
          )}
        </Section>

        <Section icon={Mic2} title={`Artistes (${includedArtists.length}${artists.length !== includedArtists.length ? ` sur ${artists.length}` : ''})`}>
          <ul className="space-y-1.5">
            {includedArtists.map((a) => {
              const p = computeArtistePrice(a.calc);
              return (
                <li key={a.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-amber-200">{a.nom_scene}</span>
                  <span className="text-[11px] text-slate-400">
                    {a.km != null && <>{a.km} km · </>}
                    coût {(Math.round((p.coutHT) * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}€ HT · vendu {(Math.round((p.venduTTC) * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}€ TTC
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 pt-3 border-t border-slate-800 space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Coût artistes total</span>
              <span className="text-slate-300">{Math.round(totalCout)} € HT</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Total proposé (calculateur)</span>
              <span className="text-amber-300 font-medium">{(Math.round((totalCalcTTC) * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} € TTC</span>
            </div>
          </div>
        </Section>
      </Card>
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5 pb-5 border-b border-slate-800 last:border-0 last:mb-0 last:pb-0">
      <div className="flex items-center gap-2 text-[10px] text-slate-400 uppercase tracking-widest mb-2.5">
        <Icon size={12} strokeWidth={1.5} className="text-amber-400" />
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function RecapRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-slate-400 text-xs shrink-0">{label}</span>
      <span className="text-slate-200 text-right whitespace-pre-wrap break-words min-w-0">{value}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

/* =========================================================
   AUTOCOMPLETE LIEU (réutilisable)
========================================================= */
function LieuAutocomplete({
  value, onChange, onPick, hasCoords,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: (s: NominatimSuggestion) => void;
  hasCoords: boolean;
}) {
  const [suggestions, setSuggestions] = useState<NominatimSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (v: string) => {
    onChange(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (v.trim().length < 3) { setSuggestions([]); return; }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchAddress(v);
        setSuggestions(data);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder="Ville, lieu, salle de réception…"
          className={inputCls + ' pr-9'}
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 size={14} className="text-amber-400 animate-spin" />
          ) : hasCoords ? (
            <Check size={14} className="text-emerald-400" strokeWidth={2} />
          ) : (
            <MapPin size={14} className="text-slate-500" strokeWidth={1.5} />
          )}
        </div>
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => { setOpen(false); setSuggestions([]); onPick(s); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/80 border-b border-slate-800/50 last:border-0 flex items-start gap-2"
            >
              <MapPin size={12} className="text-amber-400 mt-1 shrink-0" strokeWidth={1.5} />
              <span className="line-clamp-2">{s.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
